/**
 * Уровни дорожной карты — общий источник для ленты уровней и для баннеров.
 *
 * Раньше массив лежал внутри LevelDeck. Баннерам он тоже понадобился: по
 * задаче Максима 24.08.2026 они показывают ближайшие незакрытые уровни, а не
 * зашитые тексты. Держать две копии значило бы однажды поправить одну и
 * забыть про другую.
 *
 * ВАЖНО: числа и статусы образцовые. Уровней на платформе нет — есть квесты
 * внутри локаций, а это другая сущность. Когда появится ручка с прогрессом,
 * меняется источник этого массива, а вёрстка и баннеры остаются как есть.
 */

const PUBLIC = process.env.PUBLIC_URL || "";

export const LEVELS = [
  {
    key: "start",
    /* Текст для баннера наверху главной: на карточке хватает короткого
       lead, а в баннере он оставляет пустое место. */
    banner:
      "Платформа, где игра, обучение и реальный бизнес живут вместе. Проходите квесты, " +
      "собирайте свои миры и открывайте роли — кабинет раскрывается по мере того, как вы это делаете.",
    n: 1,
    title: "Первые шаги",
    lead: "Добро пожаловать в Волшебный мир",
    art: `${PUBLIC}/fw/award-license.webp`,
    passed: 1284,
    state: "active",
    /* Вводная и награда за уровень целиком — Максим 22.08.2026. Человеку,
       который пришёл по кнопке «С чего начать», нужно сперва понять, куда
       он попал и что получит, а уже потом видеть список дел. */
    intro:
      "Это первый уровень. Здесь вы знакомитесь с кабинетом: заполняете профиль, " +
      "узнаёте, как устроена платформа, и выбираете роль. Ничего покупать не нужно.",
    reward: {
      title: "Герой. Первопроходец",
      note: "Карточка роли открывает новые возможности на платформе.",
    },
    tasks: [
      {
        title: "Завести кабинет",
        done: true,
        how: "Регистрация уже пройдена — вы здесь.",
        coins: 50,
      },
      {
        title: "Подтвердить почту",
        done: false,
        how: "На почту приходят награды и чеки. Пока адрес не подтверждён, покупки могут не привязаться к кабинету.",
        coins: 50,
        to: `${PUBLIC}/profile`,
      },
      {
        title: "Заполнить профиль",
        done: false,
        how: "Имя, контакты, город. Дальше подставятся в документы — повторно вводить не придётся.",
        coins: 100,
        to: `${PUBLIC}/profile`,
      },
      {
        title: "Привязать Telegram",
        done: false,
        how: "Через него приходят уведомления о заданиях и выплатах — быстрее, чем почтой.",
        coins: 100,
        to: `${PUBLIC}/profile`,
      },
      {
        title: "Узнать, как устроена платформа",
        done: false,
        how: "Короткий разбор: проект, локации, задания и как из этого получается доход.",
        coins: 100,
        to: `${PUBLIC}/knowledge`,
      },
      {
        title: "Выбрать роль",
        done: false,
        how: "Шесть ролей: от Героя до Хранителя. Роль определяет, какие разделы откроются и на каких условиях вы участвуете.",
        coins: 150,
        to: `${PUBLIC}/role/hero`,
      },
      {
        title: "Пройти вводный курс",
        done: false,
        how: "Книга-комикс с короткими видеоуроками: от идеи до публикации своего мира.",
        coins: 200,
        to: `${PUBLIC}/learning`,
      },
    ],
  },
  {
    key: "jubilee",
    /* Текст для баннера наверху главной: на карточке хватает короткого
       lead, а в баннере он оставляет пустое место. */
    banner:
      "К юбилею выпущена Юбилейная лицензия. Забронируйте её — и получите в подарок " +
      "Flower Box с цифровыми наградами: курс, Волшебная семечка и лутбокс со стихией.",
    n: 2,
    title: "Юбилей 5 лет",
    lead: "В подарок Flower Box",
    art: `${PUBLIC}/fw/award-flowerbox.webp`,
    passed: 417,
    state: "open",
    tasks: [
      {
        title: "Посмотреть, что входит в Юбилейную лицензию",
        done: false,
        how: "Лицензия «Волшебный мир — 5 лет» и подарочный Flower Box: курс, Волшебная семечка, подарки партнёров, лутбокс со стихией.",
        coins: 50,
        to: `${PUBLIC}/instruction`,
      },
      {
        title: "Забронировать лицензию",
        done: false,
        how: "Flower Box идёт в подарок — отдельно покупать ничего не нужно.",
        award: "Бронь лицензии",
        href: "https://birthday.fairyland.world/",
      },
      {
        title: "Оплатить",
        done: false,
        how: "Оплата картой. Одна лицензия — 5000 рублей; можно взять несколько, тогда и наград будет столько же.",
        award: "Юбилейная лицензия",
        href: "https://birthday.fairyland.world/",
      },
      {
        title: "Получить чек на почту",
        done: false,
        how: "Чек приходит на адрес, указанный при оплате. Используйте ту же почту, что и в кабинете, — тогда награды привяжутся сами.",
        coins: 50,
      },
      {
        title: "Найти награды в профиле",
        done: false,
        how: "Лицензия и Flower Box появляются в разделе «Мои награды» сразу после оплаты. Если их нет через 15 минут — напишите в поддержку.",
        coins: 100,
        to: `${PUBLIC}/profile`,
      },
      {
        title: "Активировать Юбилейную лицензию",
        done: false,
        how: "Активация открывает юбилейные материалы проекта.",
        award: "Доступ к материалам",
        to: `${PUBLIC}/profile`,
      },
      {
        title: "Заполнить реквизиты",
        done: false,
        how: "Понадобятся, когда дойдёт до договора и выплат. Заполнить лучше заранее — потом подставятся сами.",
        coins: 150,
        to: `${PUBLIC}/profile`,
      },
    ],
  },
  {
    key: "license",
    n: 3,
    title: "Первый мир и первые деньги",
    lead: "Собрать мир, открыть его и получить оплату",
    art: null,
    passed: 96,
    state: "locked",
    tasks: [
      {
        title: "Активировать Flower Box",
        done: false,
        how: "Коробка раскроется, содержимое разойдётся по кабинету: курс, Волшебная семечка, подарки партнёров и лутбокс.",
        award: "Курс и семечка",
        to: `${PUBLIC}/profile`,
      },
      {
        title: "Создать проект",
        done: false,
        how: "Проект объединяет материалы, настройки и объекты вашего мира.",
        coins: 200,
        to: `${PUBLIC}/constructor`,
      },
      {
        title: "Собрать первую локацию",
        done: false,
        how: "Локация — игровая сцена: объекты, персонажи, мультимедиа. Собирается в конструкторе без программирования.",
        coins: 300,
        to: `${PUBLIC}/constructor`,
      },
      {
        title: "Добавить игровые задания",
        done: false,
        how: "Шесть форматов: кодовое слово, викторина, модерация, AR GEO, AR Marker, регистрация в Telegram.",
        coins: 300,
        to: `${PUBLIC}/constructor`,
      },
      {
        title: "Назначить награды за задания",
        done: false,
        how: "За что игрок получает монеты и цифровые награды. Без наград мир проходят один раз и забывают.",
        coins: 200,
        to: `${PUBLIC}/constructor`,
      },
      {
        title: "Опубликовать мир",
        done: false,
        how: "Мир становится доступен игрокам и появляется в разделе «Миры».",
        award: "Свой мир на платформе",
        to: `${PUBLIC}/location-list`,
      },
      {
        title: "Получить первую оплату на счёт",
        done: false,
        how: "Доход с мира приходит на баланс, оттуда выводится на расчётный счёт по реквизитам из профиля.",
        award: "Первая выплата",
        to: `${PUBLIC}/wallet`,
      },
    ],
  },
  {
    key: "grow",
    n: 4,
    title: "Рост",
    lead: "Команда, продвижение и постоянный доход",
    art: `${PUBLIC}/fw/award-flowerbox.webp`,
    passed: 63,
    state: "locked",
    tasks: [
      {
        title: "Открыть лутбокс и получить карточку стихии",
        done: false,
        how: "Внутри одна случайная карточка: Огонь, Вода, Земля или Воздух. Четыре вместе дают день проживания в Резиденции.",
        award: "Карточка стихии",
        to: `${PUBLIC}/profile`,
      },
      {
        title: "Позвать первых игроков",
        done: false,
        how: "Мир без игроков дохода не приносит. Начните с тех, кому он интересен по теме.",
        coins: 200,
      },
    ],
  },
];

/** Уровни, до которых человеку ещё идти: ими и заняты баннеры наверху. */
export const nextLevels = (count) =>
  LEVELS.filter((l) => l.state !== "done").slice(0, count);

export default LEVELS;
